<template>
  <dialog ref="dialog" aria-labelledby="assignment-editor-title" aria-describedby="assignment-editor-help"
      class="assignment-editor w-[calc(100%-2rem)] max-w-xl rounded-2xl border border-slate-200 bg-white p-0 text-left text-slate-900 shadow-2xl dark:border-slate-700 dark:bg-slate-900 dark:text-white"
      @cancel.prevent="close" @close="restoreFocus">
    <form @submit.prevent="save">
      <header class="flex items-start justify-between gap-4 px-6 pt-6 sm:px-8 sm:pt-8">
        <div>
          <h2 id="assignment-editor-title" class="text-2xl font-bold tracking-tight">{{ assignment ? 'Edit assignment' : 'New custom assignment' }}</h2>
          <p id="assignment-editor-help" class="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">Keep track of work you add yourself. You can edit it anytime.</p>
        </div>
        <button type="button" aria-label="Close editor" :disabled="saving" @click="close" class="editor-close rounded-lg p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800">
          <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m6 6 12 12M6 18 18 6" /></svg>
        </button>
      </header>
      <fieldset :disabled="saving" class="space-y-5 px-6 py-6 sm:px-8">
        <div>
          <label for="assignment-title" class="editor-label">Assignment title <span class="editor-optional">Optional</span></label>
          <input id="assignment-title" v-model="draft.short_description" autofocus class="editor-input" placeholder="e.g., Finish chapter 4 questions" />
        </div>
        <div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label for="assignment-section" class="editor-label">Class or section <span class="editor-optional">Optional</span></label>
            <input id="assignment-section" v-model="draft.section_name" class="editor-input" placeholder="e.g., Biology" />
          </div>
          <div>
            <label for="assignment-due" class="editor-label">Due date</label>
            <input id="assignment-due" v-model="draft.date_due" type="date" required class="editor-input" aria-describedby="assignment-date-help" />
            <p id="assignment-date-help" class="mt-2 text-xs text-slate-500 dark:text-slate-400">Choose a weekday.</p>
          </div>
        </div>
        <div>
          <label for="assignment-notes" class="editor-label">Notes <span class="editor-optional">Optional</span></label>
          <textarea id="assignment-notes" v-model="draft.long_description" rows="4" class="editor-input resize-y" placeholder="Instructions, reminders, or anything you need to remember…"></textarea>
        </div>
        <p v-if="error" role="alert" class="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-950 dark:text-red-200">{{ error }}</p>
      </fieldset>
      <footer class="flex justify-end gap-3 border-t border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-700 dark:bg-slate-800/50 sm:px-8">
        <button type="button" :disabled="saving" @click="close" class="editor-button border border-slate-300 bg-white text-slate-700 hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200">Cancel</button>
        <button type="submit" :disabled="saving" class="editor-button bg-indigo-600 text-white hover:bg-indigo-700">{{ saving ? 'Saving…' : assignment ? 'Save changes' : 'Create assignment' }}</button>
      </footer>
    </form>
  </dialog>
</template>

<script>
import axios from 'axios';
import { backendUrl } from '@/config/backend';

export default {
  props: { assignment: { type: Object, default: null }, token: { type: String, required: true } },
  emits: ['close', 'saved'],
  data() {
    return {
      draft: {
        section_name: this.assignment?.section_name || '',
        short_description: this.assignment?.short_description || '',
        long_description: this.assignment?.long_description || '',
        date_due: this.assignment?.date_due?.split('T')[0] || '',
      },
      saving: false,
      error: '',
      previousFocus: null,
    };
  },
  mounted() {
    this.previousFocus = document.activeElement;
    this.$refs.dialog.showModal();
  },
  beforeUnmount() { this.restoreFocus(); },
  methods: {
    restoreFocus() { this.previousFocus?.focus?.(); },
    close() { if (!this.saving) this.$emit('close'); },
    async save() {
      if (this.saving) return;
      this.error = '';
      const payload = Object.fromEntries(Object.entries(this.draft).map(([key, value]) => [key, value.trim()]));
      if (!payload.section_name && !payload.short_description && !payload.long_description) {
        this.error = 'Add a title, class, or note so you can recognize this assignment.';
        return;
      }
      const due = new Date(`${payload.date_due}T00:00:00`);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const unchangedDate = this.assignment?.date_due?.split('T')[0] === payload.date_due;
      if (Number.isNaN(due.getTime()) || (!unchangedDate && (due < today || [0, 6].includes(due.getDay())))) {
        this.error = 'Choose a weekday today or later. Existing due dates can stay as they are.';
        return;
      }
      payload.section_name ||= 'Custom Assignment';
      payload.short_description ||= 'Custom Assignment';
      payload.long_description ||= 'No additional details provided.';
      this.saving = true;
      try {
        const config = { headers: { Authorization: `Bearer ${this.token}` } };
        const response = this.assignment
          ? await axios.put(backendUrl(`/custom_assignments/${encodeURIComponent(this.assignment.assignment_id)}`), payload, config)
          : await axios.post(backendUrl('/custom_assignments'), payload, config);
        this.$emit('saved', { ...this.assignment, ...response.data, user_task: true, status: this.assignment?.status || 'not-started' });
      } catch {
        this.error = 'Your assignment could not be saved. Your changes are still here; please try again.';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.assignment-editor { max-height: 90dvh; overflow-y: auto; overscroll-behavior: contain; }
.assignment-editor::backdrop { background: rgb(15 23 42 / 55%); backdrop-filter: blur(4px); }
.editor-label { @apply mb-2 flex flex-wrap items-baseline gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200; }
.editor-optional { @apply text-xs font-normal text-slate-500 dark:text-slate-400; }
.editor-input { @apply block w-full min-w-0 rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-base text-slate-900 placeholder-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 dark:border-slate-600 dark:bg-slate-800 dark:text-white; }
.editor-button { @apply rounded-lg px-4 py-2.5 text-sm font-semibold disabled:cursor-wait disabled:opacity-60; }
.editor-button:focus-visible, .editor-close:focus-visible { outline: 2px solid #6366f1; outline-offset: 3px; }
:global(.dark) input[type='date'] { color-scheme: dark; }
</style>
